import { EntityType } from 'breeze-client';
import { BxLogger } from '../../../core/services/bx-logger.service';
import { LaunchDarklyService } from '../../../core/services/launchdarkly.service';
import { PurchaseOrder } from '../../../data/models';
import { JobsInvoiceSettingsService } from '../../../data/repositories/jobs/jobs-invoice-settings.service';
import { RepoCommonService } from '../../../data/repositories/repo-common.service';
import { RepoJobsService } from '../../../data/repositories/repo-jobs.service';
import { BreezeService } from '../../../data/services/breeze/breeze.service';
import { SharedDataService } from '../../../data/services/shared-data.service';

export abstract class PurchaseOrdersServiceBase {
    constructor(
        protected breezeService: BreezeService,
        protected lds: LaunchDarklyService,
        protected sharedData: SharedDataService,
        protected repoJobs: RepoJobsService,
        protected repoCommon: RepoCommonService,
        protected jobsSettingsService: JobsInvoiceSettingsService,
        protected logger: BxLogger
    ) {}

    public async saveOrder(order: PurchaseOrder) {
        let msg = '';
        if (order.OrderType === 'purchase') {
            msg = 'Purchase Order saved';
        } else if (order.OrderType === 'work') {
            msg = 'Work Order saved';
        }

        if (order.entityAspect.entityState.isAdded()) {
            await this.saveNewOrder(order, msg);
            return order;
        } else {
            await this.saveExistingOrder(order, msg);
            return order;
        }
    }

    private async saveNewOrder(order: PurchaseOrder, msg?: string) {
        // Save order entity first, to prevent foreign key save issues in the backend
        await this.repoJobs.Save(order);
        // @ts-ignore TS7034
        const entities = [];
        const orderItemType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderItem') as EntityType;
        const orderInvoiceType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderInvoice') as EntityType;
        const orderInvoiceItemType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderInvoiceItem') as EntityType;
        const changes = this.breezeService.manager.getChanges([orderItemType, orderInvoiceType, orderInvoiceItemType]);
        // @ts-ignore TS7005
        Array.prototype.push.apply(entities, changes);
        // @ts-ignore TS7005
        await this.repoJobs.Save(entities, msg);
        return order;
    }

    private async saveExistingOrder(order: PurchaseOrder, msg?: string): Promise<PurchaseOrder> {
        const entities = [];
        entities.push(order);
        const orderItemType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderItem') as EntityType;
        const orderInvoiceType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderInvoice') as EntityType;
        const orderInvoiceItemType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderInvoiceItem') as EntityType;
        const changes = this.breezeService.manager.getChanges([orderItemType, orderInvoiceType, orderInvoiceItemType]);
        Array.prototype.push.apply(entities, changes);
        await this.repoJobs.Save(entities, msg);
        return order;
    }

    public async syncOrder(order: PurchaseOrder): Promise<PurchaseOrder> {
        await this.repoJobs.SyncJobItemsWithPurchaseOrders(order.Job);
        await this.repoJobs.Save();
        return order;
    }

    public cancelOrder(order: PurchaseOrder) {
        const orderItemType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderItem') as EntityType;
        const orderInvoiceType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderInvoice') as EntityType;
        const orderInvoiceItemType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderInvoiceItem') as EntityType;
        const changes = this.breezeService.manager.getChanges([orderItemType, orderInvoiceType, orderInvoiceItemType]);
        changes.forEach((item) => {
            item.entityAspect.rejectChanges();
        });
        order.entityAspect.rejectChanges();
    }

    public async setOrderNumber(order: PurchaseOrder): Promise<PurchaseOrder> {
        if (order.OrderNumber === 0) {
            const orderNumber = (await this.jobsSettingsService.newOrderNumber().toPromise())?.data?.newPurchaseOrderNumber;
            if (orderNumber) {
                order.OrderNumber = orderNumber as number;
            } else {
                const errorMsg = 'Could not get new order number';
                this.logger.error(errorMsg);
                throw new Error(errorMsg);
            }
            return order;
        } else return order;
    }
}
